import React, { useState, useCallback, useEffect, useRef } from 'react';
import { getBackendUrl } from '../../utils/api';
import storage from '../../utils/storage';

const BACKEND_URL = getBackendUrl();
const getToken = () => storage.getItem('token');

/**
 * StationsSyncPanel — Admin tool for backing up + syncing station configs
 * between environments (production ↔ preview).
 *
 * Why this exists: the support agent investigating ranking/audience bugs
 * needs to reproduce the exact dataset locally, which means having the
 * same station list with the same `stats_url`/`stream_url` configs.
 *
 * Backed by:
 *   GET  /api/admin/config/stations/export/download
 *   POST /api/admin/config/stations/import
 */
const StationsSyncPanel = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState(null);
  const fileInputRef = useRef(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/config/stations/export`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (r.ok) {
        const data = await r.json();
        setStats({ count: data.count, exportedAt: data.exported_at });
      }
    } catch (e) {
      // Silent — fetched on-demand in download/import paths anyway
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleDownload = () => {
    // Trigger via authenticated XHR to keep the JWT in the request,
    // then save the blob to disk.
    setLoading(true);
    setMessage(null);
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${BACKEND_URL}/api/admin/config/stations/export/download`);
    xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);
    xhr.responseType = 'blob';
    xhr.onload = () => {
      setLoading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        const count = xhr.getResponseHeader('X-Stations-Count') || stats?.count || '?';
        // Derive filename from Content-Disposition or fall back
        const cd = xhr.getResponseHeader('Content-Disposition') || '';
        const m = /filename="?([^";]+)"?/.exec(cd);
        const filename = m ? m[1] : `stations_export_${count}.json`;
        const url = window.URL.createObjectURL(xhr.response);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        setMessage({ type: 'success', text: `✅ Έγινε λήψη ${count} σταθμών (${filename})` });
      } else {
        setMessage({ type: 'error', text: `❌ Σφάλμα λήψης: HTTP ${xhr.status}` });
      }
    };
    xhr.onerror = () => {
      setLoading(false);
      setMessage({ type: 'error', text: '❌ Σφάλμα δικτύου' });
    };
    xhr.send();
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Read JSON
    let payload;
    try {
      const text = await file.text();
      payload = JSON.parse(text);
    } catch (err) {
      setMessage({ type: 'error', text: `❌ Μη έγκυρο JSON: ${err.message}` });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Accept both shapes: {stations: [...]} (from export) or a bare list
    const stations = Array.isArray(payload) ? payload : payload.stations;
    if (!Array.isArray(stations) || stations.length === 0) {
      setMessage({ type: 'error', text: '❌ Το αρχείο δεν περιέχει stations' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Two-step confirm: merge by default, replace requires typed confirmation
    const wantsReplace = window.confirm(
      `Εισαγωγή: ${file.name} (${stations.length} σταθμοί)\n\n` +
      '✅ OK = ΣΥΓΧΩΝΕΥΣΗ (smart upsert by id — ασφαλές)\n\n' +
      '⚠️ Cancel = ΑΝΤΙΚΑΤΑΣΤΑΣΗ (διαγράφει ΟΛΟΥΣ τους τρέχοντες σταθμούς πρώτα)'
    ) === false;

    let force = false;
    if (wantsReplace) {
      const totalNow = stats?.count ?? '?';
      const typed = window.prompt(
        `⚠️ ΠΡΟΣΟΧΗ — ΜΗ ΑΝΑΣΤΡΕΨΙΜΗ ΕΝΕΡΓΕΙΑ\n\n` +
        `Θα διαγραφούν ${totalNow} τρέχοντες σταθμοί και θα παραμείνουν μόνο όσοι είναι στο αρχείο "${file.name}".\n\n` +
        `Για να συνεχίσεις, πληκτρολόγησε: ΔΙΑΓΡΑΦΗ`
      );
      if (typed !== 'ΔΙΑΓΡΑΦΗ') {
        setMessage({ type: 'info', text: 'Ακυρώθηκε — οι σταθμοί παραμένουν ως είχαν.' });
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      force = true;
    }

    setImporting(true);
    setMessage(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/config/stations/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ stations, force_replace: force }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      const parts = [`+${data.inserted} νέοι`];
      if (data.updated > 0) parts.push(`↻ ${data.updated} ενημερώθηκαν`);
      if (data.skipped_no_id > 0) parts.push(`⏭ ${data.skipped_no_id} skipped (no id/name)`);
      setMessage({
        type: 'success',
        text: `✅ Εισαγωγή σταθμών: ${parts.join(' · ')} · σύνολο ${data.total_in_db}${data.force_replaced ? ' (αντικατάσταση)' : ''}`,
      });
      fetchStats();
    } catch (err) {
      setMessage({ type: 'error', text: `❌ Σφάλμα εισαγωγής: ${err.message}` });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const messageColors = {
    success: 'bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-900/20 dark:border-emerald-700 dark:text-emerald-200',
    error:   'bg-red-50 border-red-300 text-red-800 dark:bg-red-900/20 dark:border-red-700 dark:text-red-200',
    info:    'bg-blue-50 border-blue-300 text-blue-800 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-200',
  };

  return (
    <div className="space-y-4" data-testid="stations-sync-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            🔄 Συγχρονισμός Σταθμών (Production ↔ Preview)
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Λήψη όλων των σταθμών ως JSON για backup ή για να γίνει αντιγραφή σε άλλο περιβάλλον.
            Ο agent μπορεί να αναπαράξει bugs με τα ίδια ακριβώς δεδομένα.
          </p>
        </div>
        {stats && (
          <div className="text-right text-xs text-gray-500 dark:text-gray-400 shrink-0">
            <div>Σταθμοί στη βάση: <strong className="text-gray-700 dark:text-gray-200">{stats.count}</strong></div>
            {stats.exportedAt && (
              <div className="mt-0.5">Τελ. έλεγχος: {new Date(stats.exportedAt).toLocaleString('el-GR')}</div>
            )}
          </div>
        )}
      </div>

      {message && (
        <div className={`p-3 rounded-md border text-sm ${messageColors[message.type] || messageColors.info}`}>
          {message.text}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Download (export) */}
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
          <h4 className="font-semibold text-emerald-800 dark:text-emerald-200 mb-2">📥 Λήψη Αναφοράς</h4>
          <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-3">
            Κατεβάζει JSON αρχείο με όλους τους σταθμούς (id, name, frequency, prefecture, stream_url, stats_url, ρυθμίσεις).
            Στείλε το στον agent για ανάλυση/testing.
          </p>
          <button
            onClick={handleDownload}
            disabled={loading}
            className={`px-4 py-2 rounded-md text-white text-sm font-medium transition-colors ${
              loading ? 'bg-emerald-400 cursor-wait' : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
            data-testid="stations-download-btn"
          >
            {loading ? '⏳ Λήψη...' : '📥 Λήψη JSON'}
          </button>
        </div>

        {/* Import */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">📤 Εισαγωγή Σταθμών</h4>
          <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
            Ανέβασε JSON αρχείο για να γίνει smart-merge ή αντικατάσταση των σταθμών.
            Smart upsert by id — αν υπάρχουν, ενημερώνονται· αν όχι, δημιουργούνται.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImport}
            disabled={importing}
            className="hidden"
            data-testid="stations-import-input"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className={`px-4 py-2 rounded-md text-white text-sm font-medium transition-colors ${
              importing ? 'bg-blue-400 cursor-wait' : 'bg-blue-600 hover:bg-blue-700'
            }`}
            data-testid="stations-import-btn"
          >
            {importing ? '⏳ Εισαγωγή...' : '📤 Επιλογή JSON'}
          </button>
        </div>
      </div>

      <details className="text-xs text-gray-500 dark:text-gray-400">
        <summary className="cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">ℹ️ Τι περιέχει το export;</summary>
        <div className="mt-2 pl-3 space-y-0.5 leading-relaxed">
          <p>Configuration-only fields (όχι runtime data):</p>
          <code className="block bg-gray-100 dark:bg-gray-800 p-2 rounded font-mono text-[11px] mt-1">
            id, name, frequency, prefecture, website,<br/>
            stream_url, stats_url, metadata_url, metadata_api_url,<br/>
            logo, description, genre, is_active, is_recording,<br/>
            auto_polling_enabled, schedule_start, schedule_end,<br/>
            broadcast_mode, recording_mode, tier_override, ...
          </code>
          <p className="mt-2">Runtime/cached δεδομένα (last_seen_at, listener history, _id) ΔΕΝ συμπεριλαμβάνονται — ο monitoring service θα τα ξαναδημιουργήσει αυτόματα.</p>
        </div>
      </details>
    </div>
  );
};

export default StationsSyncPanel;
